import { ConvertTraditional } from '../components/KeywordTranslator';

const BOOK_NAMES = [
  ['創世紀', '創'], ['創世記', '創'], ['出埃及記', '出'], ['利未記', '利'], ['民數記', '民'], ['申命記', '申'],
  ['約書亞記', '書'], ['士師記', '士'], ['路得記', '得'], ['撒母耳記上', '撒上'], ['撒母耳記下', '撒下'],
  ['列王記上', '王上'], ['列王紀上', '王上'], ['列王記下', '王下'], ['列王紀下', '王下'],
  ['歷代志上', '代上'], ['歷代志下', '代下'], ['以斯拉記', '拉'], ['尼希米記', '尼'], ['以斯帖記', '斯'],
  ['約伯記', '伯'], ['詩篇', '詩'], ['箴言', '箴'], ['傳道書', '傳'], ['雅歌', '歌'], ['以賽亞書', '賽'],
  ['耶利米書', '耶'], ['耶利米哀歌', '哀'], ['以西結書', '結'], ['但以理書', '但'], ['何西阿書', '何'],
  ['約珥書', '珥'], ['阿摩司書', '摩'], ['俄巴底亞書', '俄'], ['約拿書', '拿'], ['彌迦書', '彌'],
  ['那鴻書', '鴻'], ['哈巴谷書', '哈'], ['西番雅書', '番'], ['哈該書', '該'], ['撒迦利亞書', '亞'],
  ['瑪拉基書', '瑪'], ['馬太福音', '太'], ['馬可福音', '可'], ['路加福音', '路'], ['約翰福音', '約'],
  ['使徒行傳', '徒'], ['羅馬書', '羅'], ['哥林多前書', '林前'], ['哥林多後書', '林後'], ['加拉太書', '加'],
  ['以弗所書', '弗'], ['腓立比書', '腓'], ['歌羅西書', '西'], ['帖撒羅尼迦前書', '帖前'], ['帖撒羅尼迦後書', '帖後'],
  ['提摩太前書', '提前'], ['提摩太後書', '提後'], ['提多書', '多'], ['腓利門書', '門'], ['希伯來書', '來'],
  ['雅各書', '雅'], ['彼得前書', '彼前'], ['彼得後書', '彼後'], ['約翰一書', '約一'], ['約翰壹書', '約一'],
  ['約翰二書', '約二'], ['約翰貳書', '約二'], ['約翰三書', '約三'], ['約翰參書', '約三'], ['猶大書', '猶'], ['啟示錄', '啟'],
];

const BOOK_ALIASES = new Map(BOOK_NAMES);
BOOK_NAMES.forEach(([, shortName]) => BOOK_ALIASES.set(shortName, shortName));
BOOK_ALIASES.set('約壹', '約一');
BOOK_ALIASES.set('約貳', '約二');
BOOK_ALIASES.set('約參', '約三');

const BOOK_FULL_NAMES = new Map(BOOK_NAMES.map(([fullName, shortName]) => [shortName, fullName]));
export const BIBLE_BOOK_ORDER = [...new Set(BOOK_NAMES.map(([, shortName]) => shortName))];

export const normalizeBookName = value => {
  const traditional = ConvertTraditional(String(value || '')).replace(/\s+/g, '');
  return BOOK_ALIASES.get(traditional) || null;
};

export const getBibleBookFullName = shortName => BOOK_FULL_NAMES.get(shortName) || shortName;

export const parseScriptureReference = value => {
  const label = String(value || '').trim();
  const match = label.match(/^(.+?)\s*(\d+)\s*[:：]\s*(\d+)(?:\s*[-－–—]\s*(\d+))?$/);
  if (!match) return null;

  const book = normalizeBookName(match[1]);
  if (!book) return null;

  const chapter = Number(match[2]);
  const verseStart = Number(match[3]);
  const verseEnd = match[4] ? Number(match[4]) : verseStart;
  if (chapter < 1 || verseStart < 1 || verseEnd < verseStart) return null;

  return {
    label,
    book,
    chapter,
    verseStart,
    verseEnd,
    verseRange: verseStart === verseEnd ? String(verseStart) : `${verseStart}-${verseEnd}`,
  };
};

const buildVerseReference = (label, book, chapter, verseStart, verseEnd = verseStart) => {
  if (!book || chapter < 1 || verseStart < 1 || verseEnd < verseStart) return null;
  return {
    label,
    book,
    chapter,
    verseStart,
    verseEnd,
    verseRange: verseStart === verseEnd ? String(verseStart) : `${verseStart}-${verseEnd}`,
  };
};

const parseInheritedScriptureReference = (label, previousReference) => {
  if (!previousReference) return null;

  // Examples: "8:18-20" keeps the preceding book but changes chapter;
  // "25" and "28-29" keep both the preceding book and chapter.
  const chapterVerseMatch = label.match(/^(\d+)\s*[:：]\s*(\d+)(?:\s*[-－–—]\s*(\d+))?$/);
  if (chapterVerseMatch) {
    return buildVerseReference(
      label,
      previousReference.book,
      Number(chapterVerseMatch[1]),
      Number(chapterVerseMatch[2]),
      chapterVerseMatch[3] ? Number(chapterVerseMatch[3]) : Number(chapterVerseMatch[2]),
    );
  }

  const verseOnlyMatch = label.match(/^(\d+)(?:\s*[-－–—]\s*(\d+))?$/);
  if (verseOnlyMatch) {
    return buildVerseReference(
      label,
      previousReference.book,
      previousReference.chapter,
      Number(verseOnlyMatch[1]),
      verseOnlyMatch[2] ? Number(verseOnlyMatch[2]) : Number(verseOnlyMatch[1]),
    );
  }

  return null;
};

export const parseScriptureReferenceList = value => {
  let previousReference = null;

  return String(value || '')
    .split(/[,，;；]/)
    .map(part => part.trim())
    .filter(Boolean)
    .map(label => {
      const reference = parseScriptureReference(label)
        || parseInheritedScriptureReference(label, previousReference);
      if (reference) previousReference = reference;
      return { label, reference };
    });
};
export const parseReadingPlanReference = value => {
  const label = String(value || '').trim();
  const firstPart = label.split(/[,，;；]/).map(part => part.trim()).find(Boolean);
  if (!firstPart) return null;

  const match = firstPart.match(/^(.+?)\s*(\d+)(?:\s*[-－–—]\s*(\d+))?(?:\s*[:：]\s*(\d+)(?:\s*[-－–—]\s*(\d+))?)?$/);
  if (!match) return null;

  const book = normalizeBookName(match[1]);
  if (!book) return null;
  const chapter = Number(match[2]);
  const chapterEnd = match[3] ? Number(match[3]) : chapter;
  const verseStart = match[4] ? Number(match[4]) : null;
  const verseEnd = match[5] ? Number(match[5]) : verseStart;
  if (chapter < 1 || chapterEnd < chapter) return null;
  if (verseStart !== null && (verseStart < 1 || verseEnd < verseStart)) return null;

  return {
    label,
    book,
    bookname: getBibleBookFullName(book),
    chapter,
    chapterEnd,
    verseStart,
    verseEnd,
  };
};

export const parseReadingPlanReferenceList = value => {
  let previousBook = null;

  return String(value || '')
    .split(/[,，;；]/)
    .map(part => part.trim())
    .filter(Boolean)
    .map(label => {
      let reference = parseReadingPlanReference(label);

      // Allow compact lists such as "羅3:21-26,5:1-11" by inheriting the
      // book name from the preceding reference.
      if (!reference && previousBook && /^\d/.test(label)) {
        reference = parseReadingPlanReference(`${previousBook}${label}`);
      }

      if (!reference) return { label, reference: null };
      previousBook = reference.book;
      return { label, reference: { ...reference, label } };
    });
};
